import {
	getMomentTz,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";

export interface Task {
	id?: number;
	WorkOrderId?: number;
	RootAssetId: number | null;
	AssetName?: string;
	AssetId?: number;
	Title?: string;
	DownHours?: number | null;
	DownDayCode?: string | null;
	CraftName?: string | null;
	CraftColor?: string | null;
	CraftId?: number;
	TypeCode?: string | null;
	Comments?: null | string;
	Close?: number;
	Ordinal?: number;
	Priority?: number;
	WorkOrderStatusCode?: string;
	OpenTimestamp?: Date;
	RunningTime?: number;
	TemplateTitle?: null | string;
	WorkOrderName?: string;
	WorkOrderPlannedHours?: number | null;
	WorkOrderNumberOfWorkers?: number | null;
	WorkOrderOpenedBy?: string;
	AttachmentsLinked?: number;
	FullPath?: string;
	Table?: number;
	TableName?: string;
}

export interface GetTaskListParams {
	craftSearchId: string[];
	typeCode: string | null;
	searchText: string | null;
	downHours: number | string | null;
	assetSearchId: number | string | null;
}

const moment = getMomentTz();

// React Query queryFn: throws on error, returns the work-order rows (tables[0]).
export const fetchTaskList = async ({
	craftSearchId,
	typeCode,
	searchText,
	downHours,
	assetSearchId,
}: GetTaskListParams): Promise<Task[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	if (craftSearchId && !craftSearchId.includes("A")) {
		parameters.push({ name: "CraftIds", value: craftSearchId.join(",") });
	} else {
		parameters.push({ name: "CraftIds", value: null });
	}

	if (typeCode !== "A") {
		parameters.push({ name: "TypeCode", value: typeCode });
	} else {
		parameters.push({ name: "TypeCode", value: null });
	}

	parameters.push({ name: "SearchText", value: searchText });
	parameters.push({
		name: "DownHours",
		value:
			downHours === "" || downHours === null ? null : Number(downHours),
	});
	parameters.push({ name: "AssetId", value: assetSearchId });

	const resp = await apiService.callV2("[MMS].[GetTaskListByArea]", parameters);
	if (!resp.ok) throw new Error(resp.message || "Error fetching task list");

	const rows = get(resp, "data.tables[0].rows", []) as Task[];
	return rows.map((row) => ({
		...row,
		OpenTimestamp: row.OpenTimestamp
			? moment.utc(row.OpenTimestamp).toDate()
			: undefined,
	}));
};

export const getCrafts = async () => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[MMS].[GetCrafts]", []);
	return resp;
};

export interface GeneratableDownDayPM {
	TemplateId: number;
	Title: string;
	AssetName: string | null;
	CraftName: string | null;
	CraftColor: string | null;
	CraftCode: string | null;
	SchedulingPolicyCode: string | null;
	DownDayCode: string | null;
	// Name of the template's open work order (e.g. PM000204), null when it has none.
	OpenWorkOrderName: string | null;
	// 1/true when the template already has an open work order: shown disabled and not
	// selectable in the picker (it cannot be generated again until that order is closed).
	HasOpenOrder: boolean | number;
}

export const getGeneratableDownDayPMs = async (
	assetSearchId: number | string | null,
): Promise<GeneratableDownDayPM[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{
			name: "AssetId",
			value:
				assetSearchId === "" || assetSearchId === null ? null : assetSearchId,
		},
	];

	const resp = await apiService.callV2(
		"[MMS].[GetGeneratableDownDayPMs]",
		parameters,
	);
	if (!resp.ok)
		throw new Error(resp.message || "Error fetching down day PMs");

	return get(resp, "data.tables[0].rows", []) as GeneratableDownDayPM[];
};

export const createDownDayPMOrders = async (
	assetSearchId: number | string | null,
	templateIds: number[] | null = null,
	downDayCode: string | null = null,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{
			name: "AssetId",
			value:
				assetSearchId === "" || assetSearchId === null ? null : assetSearchId,
		},
		{
			name: "TemplateIds",
			value:
				templateIds && templateIds.length > 0 ? templateIds.join(",") : null,
		},
		{ name: "DownDayCode", value: downDayCode },
	];

	const resp = await apiService.callV2(
		"[MMS].[CreateDownDayPMOrders]",
		parameters,
	);
	if (!resp.ok)
		throw new Error(resp.message || "Error creating down day orders");
};

// TargetCategory selects the column the order is dropped into:
// 'T' = Down Time, 'D' = Down Day, 'R' = Routine, null = in-list reorder only.
export const UpdateTaskListPriority = async (
	WorkOrderIdToMove: number,
	WorkOrderIdMoveBelow: number | null,
	TargetCategory: string | null,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "WorkOrderIdToMove", value: WorkOrderIdToMove },
		{ name: "WorkOrderIdMoveBelow", value: WorkOrderIdMoveBelow },
		{ name: "TargetCategory", value: TargetCategory },
	];

	const resp = await apiService.callV2(
		"[MMS].[UpdateTaskListPriority]",
		parameters,
	);
	if (!resp.ok)
		throw new Error(resp.message || "Error updating priority");
};
